// Durchbiegung eines Fachbodens — reine Rechnung, engine-frei.
//
// Modell: Einfeldträger auf zwei Auflagern mit gleichmäßig verteilter Last
// (der Bücher-/Geschirrstapel liegt über die ganze Bodenfläche). Biegelinie in
// Feldmitte:
//
//     f = 5 · q · L⁴ / (384 · E · I)      mit I = b · h³ / 12
//
//   f = Durchbiegung [mm], q = Streckenlast [N/mm], L = freie Spannweite [mm],
//   E = Elastizitätsmodul [N/mm²], I = Flächenträgheitsmoment [mm⁴],
//   b = Bodentiefe [mm], h = Plattenstärke [mm].
//
// Die Streckenlast entsteht aus der Flächenlast: q = p · b. Rechnerisch kürzt
// sich b damit heraus (f hängt nur von L, h und E ab) — die Tiefe bleibt
// trotzdem im Aufruf, weil sie den Rechenweg lesbar hält und die Formel damit
// auch für eine punktuell eingegebene Streckenlast taugt.
//
// Die Rechnung ist bewusst auf der sicheren Seite: Nut/Dübel in den Seiten
// wirken wie Gelenke, also wird mit freier Auflagerung gerechnet (weichster
// Fall). Eine Einspannung würde die Durchbiegung auf bis zu ein Fünftel senken.

// Praxislast: 50 kg/m² entspricht einem dicht gefüllten Bücherbord
// (Taschenbücher ≈ 40 kg/m², gebundene Bücher ≈ 60 kg/m²). Küchen-/Geschirr-
// böden liegen darunter, Aktenordner darüber — 50 kg/m² ist der übliche
// Auslegungswert im Möbelbau (vgl. DIN 68874 / EN 16121 „schwere Nutzung“).
export const SHELF_LOAD_KG_PER_SQM = 50

// Erdbeschleunigung, um die Masse in eine Kraft umzurechnen
export const GRAVITY_N_PER_KG = 9.81

// Grenzwert: Durchbiegung über L/300 ist mit bloßem Auge als „durchhängend“
// erkennbar und gilt in der Schreinerei als Reklamationsgrund. Bauteile werden
// üblicherweise auf L/300 (sichtbare Verformung) bis L/500 (Sichtkante)
// ausgelegt; für ein Möbelbrett ist L/300 die gängige Faustregel — bei 800 mm
// Spannweite also 2,7 mm.
export const DEFLECTION_LIMIT_DIVISOR = 300

// Biege-E-Moduln in N/mm². Richtwerte aus den Werkstoffnormen bzw. den
// Herstellerangaben der Plattenwerke (Spanplatte EN 312 P2: 1600–2800;
// MDF EN 622-5: 2500–3500; Multiplex/Sperrholz Birke längs: 5000–9000;
// Nadel-/Laubholz Massiv längs: 10000–12000). Gewählt ist jeweils ein
// bewusst unterer, also konservativer Wert — lieber einmal zu früh warnen.
export const E_MODULI_N_PER_MM2 = {
  spanplatte: 2500,
  mdf: 3000,
  multiplex: 6000,
  massivholz: 10000
}

// Ohne erkennbares Material wird mit dem weichsten Werkstoff gerechnet:
// Spanplatte ist im Möbelbau der Normalfall und der ungünstigste der vier.
export const DEFAULT_E_MODULUS_N_PER_MM2 = E_MODULI_N_PER_MM2.spanplatte

// Zuordnung Materialname → Werkstoffgruppe. Reihenfolge ist Priorität:
// „Dreischichtplatte Fichte“ und „Multiplex Birke“ müssen als Lagenholz
// erkannt werden, bevor die Massivholz-Stichworte greifen.
const MATERIAL_PATTERNS = [
  [ /multiplex|sperrholz|siebdruck|tischlerplatte|dreischicht|stäbchen/, "multiplex" ],
  [ /\bmdf\b|\bhdf\b|faserplatte/, "mdf" ],
  // OSB (E ≈ 3500) läuft bewusst mit der Spanplatte: konservativ gerundet
  [ /span|melamin|\bp2\b|\bosb\b/, "spanplatte" ],
  [ /leimholz|massiv|eiche|buche|esche|nussbaum|fichte|kiefer|ahorn|lärche|birne|kirsche/, "massivholz" ]
]

// Werkstoffgruppe eines Materialnamens; null, wenn nichts passt.
export function materialGroupFor(materialName) {
  const name = String(materialName ?? "").toLowerCase()
  if (!name) return null
  for (const [ pattern, group ] of MATERIAL_PATTERNS) {
    if (pattern.test(name)) return group
  }
  return null
}

// E-Modul zum Materialnamen, mit konservativem Default.
export function eModulusFor(materialName) {
  const group = materialGroupFor(materialName)
  return group ? E_MODULI_N_PER_MM2[group] : DEFAULT_E_MODULUS_N_PER_MM2
}

// Flächenlast in N/mm² (1 m² = 1e6 mm²)
export function areaLoadNPerMm2(loadKgPerSqm = SHELF_LOAD_KG_PER_SQM) {
  return (loadKgPerSqm * GRAVITY_N_PER_KG) / 1e6
}

// Flächenträgheitsmoment eines Rechteckquerschnitts: I = b · h³ / 12
export function momentOfInertiaMm4(widthMm, thicknessMm) {
  return (widthMm * thicknessMm ** 3) / 12
}

export function deflectionLimitMm(spanMm) {
  return spanMm / DEFLECTION_LIMIT_DIVISOR
}

// Durchbiegung in mm; null, wenn ein Maß fehlt oder unsinnig ist.
export function shelfDeflectionMm({
  spanMm,
  thicknessMm,
  widthMm,
  eModulusNPerMm2 = DEFAULT_E_MODULUS_N_PER_MM2,
  loadKgPerSqm = SHELF_LOAD_KG_PER_SQM
} = {}) {
  const positive = (value) => Number.isFinite(value) && value > 0
  if (![ spanMm, thicknessMm, widthMm, eModulusNPerMm2 ].every(positive)) return null

  const lineLoadNPerMm = areaLoadNPerMm2(loadKgPerSqm) * widthMm
  const inertia = momentOfInertiaMm4(widthMm, thicknessMm)
  return (5 * lineLoadNPerMm * spanMm ** 4) / (384 * eModulusNPerMm2 * inertia)
}

// Komplette Beurteilung eines Fachbodens — auch fürs Panel gedacht.
// Liefert null, wenn sich nichts rechnen lässt.
export function assessShelf({ spanMm, thicknessMm, widthMm, materialName, loadKgPerSqm } = {}) {
  const eModulusNPerMm2 = eModulusFor(materialName)
  const deflectionMm = shelfDeflectionMm({ spanMm, thicknessMm, widthMm, eModulusNPerMm2, loadKgPerSqm })
  if (deflectionMm === null) return null
  const limitMm = deflectionLimitMm(spanMm)
  return {
    spanMm,
    deflectionMm,
    limitMm,
    eModulusNPerMm2,
    materialGroup: materialGroupFor(materialName),
    critical: deflectionMm > limitMm
  }
}
